import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../store/store';
import Card from '../../ui/Card';
import { formatCurrency } from '../../../utils/formatters';
import { TrendingUp, TrendingDown } from 'react-feather';

const SalesMetrics: React.FC = () => {
  const { t } = useTranslation();
  const { sales } = useSelector((state: RootState) => state.sales);

  const calculateMetrics = () => {
    const totalSales = sales.reduce((sum, sale) => sum + sale.total_amount, 0);
    const totalProfit = sales.reduce((sum, sale) => sum + (sale.profit || 0), 0);
    const averageSale = sales.length > 0 ? totalSales / sales.length : 0;

    // Calculate month-over-month growth
    const currentMonth = new Date().getMonth();
    const currentMonthSales = sales
      .filter(sale => new Date(sale.sale_date).getMonth() === currentMonth)
      .reduce((sum, sale) => sum + sale.total_amount, 0);
    
    const lastMonthSales = sales
      .filter(sale => new Date(sale.sale_date).getMonth() === currentMonth - 1)
      .reduce((sum, sale) => sum + sale.total_amount, 0);

    const growth = lastMonthSales ? ((currentMonthSales - lastMonthSales) / lastMonthSales) * 100 : 0;

    return {
      totalSales,
      totalProfit,
      averageSale,
      growth
    };
  };

  const metrics = calculateMetrics();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <Card>
        <h3 className="text-sm font-medium text-gray-500">{t('sales.totalAmount')}</h3>
        <p className="mt-2 text-3xl font-semibold">{formatCurrency(metrics.totalSales)}</p>
      </Card>

      <Card>
        <h3 className="text-sm font-medium text-gray-500">{t('sales.profit')}</h3>
        <p className={`mt-2 text-3xl font-semibold ${metrics.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {formatCurrency(metrics.totalProfit)}
        </p>
      </Card>

      <Card>
        <h3 className="text-sm font-medium text-gray-500">{t('sales.averageSale')}</h3>
        <p className="mt-2 text-3xl font-semibold">{formatCurrency(metrics.averageSale)}</p>
      </Card>

      <Card>
        <h3 className="text-sm font-medium text-gray-500">{t('dashboard.stats.monthlyGrowth')}</h3>
        <div className="mt-2 flex items-center">
          <p className="text-3xl font-semibold">{Math.abs(metrics.growth).toFixed(1)}%</p>
          {metrics.growth > 0 ? (
            <TrendingUp className="ml-2 text-green-500" />
          ) : (
            <TrendingDown className="ml-2 text-red-500" />
          )}
        </div>
      </Card>
    </div>
  );
};

export default SalesMetrics;